import type { EnrollmentStep, OnboardingBriefing } from '@dyspatch/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Button } from '@/components/ui/button';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { BeforeYouStart, EnrollmentSteps } from '@/features/fleet/ConnectDevice';
import { api } from '@/lib/api';
import { hubKeys } from '@/lib/query';

/**
 * The page an enrollment link opens.
 *
 * An invite used to travel as two things that had to arrive together and be
 * retyped in the right places: the hub's address and an eight-character code.
 * Every admin pasting one into Slack wrote their own covering sentence, and
 * every one of those sentences was a chance to leave something out. One link
 * carries the lot, and what it opens is the same sequence the console would
 * have shown, with this code already in the commands.
 *
 * It is `/join` rather than `/enroll` because `/enroll` is the worker's own
 * POST surface on this hub and is reserved; a client route there would never
 * be reached.
 *
 * The page never redeems anything. A link opened twice is the same link, and
 * the code is spent by `init` on the laptop that will do the work, which is the
 * only machine that should ever hold it.
 */
export function JoinPage({ code }: { code: string | null }) {
  const invite = useQuery({
    queryKey: hubKeys.enrollmentSteps(code ?? ''),
    queryFn: () => api.enrollmentSteps(code ?? ''),
    enabled: Boolean(code),
    retry: false,
  });
  const briefing = useQuery<OnboardingBriefing>({
    queryKey: hubKeys.onboarding,
    queryFn: api.onboarding,
    staleTime: 5 * 60_000,
  });

  if (!code) return <Missing title="This link has no code in it" />;
  if (invite.isPending) {
    return (
      <Page className="max-w-[820px]">
        <ViewHeader title="Connect this laptop" sub="reading the code in your link" />
      </Page>
    );
  }
  if (invite.isError || !invite.data) {
    return <Missing title="That code matches nothing on this hub" />;
  }

  const { workerName, status, expiresAt, steps } = invite.data;
  if (status !== 'open') return <Spent status={status} workerName={workerName} steps={steps} />;

  return (
    <Page className="max-w-[820px]">
      <ViewHeader
        title="Connect this laptop"
        sub={`it joins as ${workerName}, and the code expires ${new Date(expiresAt).toLocaleString()}`}
      />
      <Section>
        <SectionHead title="Run these on the laptop you want to connect" />
        <Surface>
          {briefing.data ? <BeforeYouStart briefing={briefing.data} /> : null}
          <EnrollmentSteps steps={steps} />
        </Surface>
      </Section>
    </Page>
  );
}

/**
 * A code that is used, revoked or expired, with its steps still drawn.
 *
 * Drawn rather than hidden because the commonest way to land here is somebody
 * reopening their own old link after they have already connected, and a page
 * that shows nothing but a refusal cannot be told apart from a broken one.
 */
function Spent({
  status,
  workerName,
  steps,
}: {
  status: string;
  workerName: string;
  steps: EnrollmentStep[];
}) {
  return (
    <Page className="max-w-[820px]">
      <ViewHeader title="This code is no longer open" sub={`it was for ${workerName}`} />
      <Section>
        <SectionHead
          title={
            status === 'used'
              ? 'It has already been redeemed'
              : status === 'revoked'
                ? 'It was revoked'
                : 'It expired'
          }
          meta={
            status === 'used'
              ? 'if that was you, the device is already in the fleet'
              : 'take a fresh one on Agents, or ask whoever sent this'
          }
          actions={
            <Button size="sm" variant="outline" asChild>
              <Link to="/agents">Go to Agents</Link>
            </Button>
          }
        />
        <Surface>
          <EnrollmentSteps steps={steps} />
        </Surface>
      </Section>
    </Page>
  );
}

function Missing({ title }: { title: string }) {
  return (
    <Page className="max-w-[820px]">
      <ViewHeader title={title} sub="nothing here has been spent" />
      <Section>
        <SectionHead
          title="Connect a laptop of your own instead"
          meta="Agents mints your own code and waits on the page until the device checks in"
          actions={
            <Button size="sm" asChild>
              <Link to="/agents">Go to Agents</Link>
            </Button>
          }
        />
      </Section>
    </Page>
  );
}
